//* Libraries imports
import { motion } from 'framer-motion';

//? Styles imports
import './process-skeleton.scss';

interface ProcessSkeletonProps {
  showCards?: boolean;
}

export function ProcessSkeleton({ showCards = true }: ProcessSkeletonProps) {  
    
    const skeletonVariants = {
    pulse: {
      opacity: [0.6, 1, 0.6],
      transition: {
        duration: 1.5,
        repeat: Infinity
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <motion.div
      className="process-skeleton"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header Skeleton */}
      <motion.section 
        className="pt-4 p-2 skeleton-header-section"
        variants={itemVariants}
      >
        <div className="d-flex justify-content-between align-items-center mb-4 skeleton-header">
          <div className="d-flex align-items-center">
            {/* Back Button Skeleton */}
            <motion.div
              className="skeleton-back-button me-3"
              variants={skeletonVariants}
              animate="pulse"
            />
            <div>
              {/* Title Skeleton */}
              <motion.div
                className="skeleton-title mb-2"
                variants={skeletonVariants}
                animate="pulse"
              />
              {/* Subtitle Skeleton */}
              <motion.div
                className="skeleton-subtitle"
                variants={skeletonVariants}
                animate="pulse"
              />
            </div>
          </div>
          {/* Badge Skeleton */}
          <motion.div
            className="skeleton-badge"
            variants={skeletonVariants}
            animate="pulse"
          />
        </div>

        {/* Info Card Skeleton */}
        <motion.div 
          className="skeleton-info-card mb-4"
          variants={itemVariants}
        >
          <div className="skeleton-info-header">
            <motion.div
              className="skeleton-info-title"
              variants={skeletonVariants}
              animate="pulse"
            />
          </div>
          <div className="skeleton-info-body">
            <div className="skeleton-info-grid">
              {[1, 2, 3].map((index) => (
                <div key={index} className="skeleton-info-item">
                  <div className="skeleton-info-item-header">
                    <motion.div
                      className="skeleton-info-icon"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-info-label"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                  </div>
                  <motion.div
                    className="skeleton-info-value"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.section>

      {/* Cards Skeleton */}
      {showCards && (
        <motion.div 
          className="skeleton-cards-container py-4"
          variants={itemVariants}
        >
          <div className="container">
            <div className="row g-4 justify-content-center">
              {[1, 2, 3, 4].map((index) => (
                <motion.div
                  key={index}
                  className="col-12 col-md-6 col-lg-4 col-xl-3 d-flex"
                  variants={itemVariants}
                >
                  <div className="skeleton-card">
                    <motion.div
                      className="skeleton-card-icon"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-card-title"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-card-description line-1"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-card-description line-2"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
